import type { SerializedError } from "@reduxjs/toolkit";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import type { ApiError } from "../contract.js";

/**
 * Every failing endpoint answers with `{ error: { code, message } }`, so that
 * message is what an operator should see. The fallbacks below only matter for
 * failures that never reached the API (offline, proxy down, HTML error page).
 */
export function apiErrorMessage(error: FetchBaseQueryError | SerializedError | undefined): string {
  if (!error) return "";

  if (!("status" in error)) return error.message ?? "Request failed.";

  if ("data" in error && error.data && typeof error.data === "object") {
    const body = error.data as Partial<ApiError>;
    const message = body.error?.message;
    if (typeof message === "string" && message.length > 0) return message;
  }

  switch (error.status) {
    case "FETCH_ERROR":
      return "Cannot reach the OTA server. Is it running on :3000?";
    case "TIMEOUT_ERROR":
      return "The server did not answer in time.";
    case "PARSING_ERROR":
      return `Unreadable response from the server (HTTP ${error.originalStatus}).`;
    case "CUSTOM_ERROR":
      return error.error;
    default:
      return `Request failed with HTTP ${error.status}.`;
  }
}

/**
 * Machine-readable half of the same envelope. Used where the UI has to react to
 * a specific refusal — `no_matching_devices` turns into a confirm box — rather
 * than only show the message.
 */
export function apiErrorCode(
  error: FetchBaseQueryError | SerializedError | undefined,
): string | null {
  if (!error || !("status" in error)) return null;
  if (!("data" in error) || !error.data || typeof error.data !== "object") return null;
  const body = error.data as Partial<ApiError>;
  return body.error?.code ?? null;
}
